import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { QuoteType } from '@nibtravel/shared/util/models';
import { ProductsApiEndpointService } from '@nibtravel/shared/util/url';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { toSelectObject } from './helpers/countries.helper';
import { ICountriesConfig } from './models/countries-state.model';

@Injectable()
export class CountriesDataService {
  constructor(
    private http: HttpClient,
    private productEndpointService: ProductsApiEndpointService
  ) {}

  /**
   * @description
   * Fetches countries list from products api.
   * The object is the same for single and multi trips, but the result set for
   * destinationCountries is the same.
   *
   * This function perform both calls in parallel and combine its result
   * into a single state object to avoid race condition to populate properties with same name
   */
  getCountriesConfig(brand: string): Observable<ICountriesConfig> {
    const url = this.productEndpointService.getCountriesConfigUrl(brand);
    return combineLatest([
      this.apiCall(url, QuoteType.SingleTrip),
      this.apiCall(url, QuoteType.MultiTrip),
    ]).pipe(map(([single, multi]) => this.mapApiData(single, multi)));
  }

  /**
   * @description
   * Simple abstraction to promote code reusability and readability.
   * Responsible for performing the actual api call to get countries config.
   */
  private apiCall(
    url: string,
    quoteType: QuoteType
  ): Observable<ICountriesConfig> {
    const params = new HttpParams().set('quoteType', quoteType);
    return this.http
      .get<ICountriesConfig>(url, { params })
      .pipe(catchError(() => of({} as ICountriesConfig)));
  }

  /**
   * @description
   * Abstraction that combines the result of both api call into a single object
   */
  private mapApiData(
    single: ICountriesConfig,
    multi: ICountriesConfig
  ): ICountriesConfig {
    return {
      ...single,
      destinationCountriesSelect: toSelectObject(single.destinationCountries),
      destinationRegions: multi.destinationCountries,
      destinationRegionsSelect: toSelectObject(multi.destinationCountries),
    };
  }
}
